/**
 * The typed failure of the generic DB `create` thunk (#1136).
 *
 * The thunk used to catch a backend failure (E11000 duplicate key, a validation error, a
 * network drop), remove the optimistic row, and FULFILL with the raw error as its payload.
 * `.unwrap()` therefore never threw and every caller carried on as if the document had been
 * stored (#1133: a recurring "only this one" edit excluded the occurrence from the series
 * after its replacement had failed to store). The thunk now REJECTS with a `DBCreateError`,
 * so `.unwrap()` throws it and `create.rejected.match(action)` narrows to it.
 *
 * Pure and dependency-free so it runs in the node jest env.
 */

export type DBCreateErrorKind =
  /** No signed-in user and the service is not public: nothing was sent to the server. */
  | 'not_authenticated'
  /** The server answered with an error status: the document was NOT stored. */
  | 'server_rejected'
  /** The request never got an answer (network error, timeout): the document was NOT stored. */
  | 'network'
  /** Anything else the create threw. */
  | 'unknown';

export interface DBCreateError {
  kind: DBCreateErrorKind;
  /** The Feathers service the create was for, e.g. `user-messages`. */
  service: string;
  /** HTTP status of the server's answer, when there was one. */
  status?: number;
  /** Feathers error name, e.g. `Conflict`, `BadRequest`. */
  name?: string;
  /** Server-side error code (the Feathers code, e.g. 409). */
  code?: number;
  /** Human-readable reason. Never contains the entity the caller tried to store. */
  message: string;
}

type UnknownRecord = { [key: string]: unknown };

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

const readString = (record: UnknownRecord, key: string): string | undefined => {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const readNumber = (record: UnknownRecord, key: string): number | undefined => {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const NETWORK_STATUSES: ReadonlySet<string> = new Set(['FETCH_ERROR', 'TIMEOUT_ERROR']);

export const notAuthenticatedCreateError = (service: string): DBCreateError => ({
  kind: 'not_authenticated',
  service,
  message: 'You are not signed in, so this could not be saved.',
});

/**
 * Builds a `DBCreateError` from whatever `createEntity(...).unwrap()` threw: an RTK Query
 * `FetchBaseQueryError` (`{ status, data }`, with the Feathers error body in `data`), a
 * `SerializedError`, or an `Error`. The result is plain and serializable, as `rejectWithValue`
 * requires.
 */
export const toDBCreateError = (service: string, thrown: unknown): DBCreateError => {
  if (!isRecord(thrown)) {
    return { kind: 'unknown', service, message: typeof thrown === 'string' && thrown ? thrown : 'Saving failed.' };
  }

  const status = thrown['status'];

  if (typeof status === 'number') {
    const body = thrown['data'];
    const bodyRecord = isRecord(body) ? body : undefined;
    return {
      kind: 'server_rejected',
      service,
      status,
      name: bodyRecord ? readString(bodyRecord, 'name') : undefined,
      code: bodyRecord ? readNumber(bodyRecord, 'code') : undefined,
      message: (bodyRecord && readString(bodyRecord, 'message')) ?? `The server refused to save it (HTTP ${status}).`,
    };
  }

  if (typeof status === 'string') {
    if (NETWORK_STATUSES.has(status)) {
      return { kind: 'network', service, message: readString(thrown, 'error') ?? 'Could not reach the server.' };
    }
    // PARSING_ERROR carries the real HTTP status on `originalStatus`.
    const originalStatus = readNumber(thrown, 'originalStatus');
    return {
      kind: originalStatus === undefined ? 'unknown' : 'server_rejected',
      service,
      status: originalStatus,
      message: readString(thrown, 'error') ?? 'Saving failed.',
    };
  }

  return { kind: 'unknown', service, message: readString(thrown, 'message') ?? 'Saving failed.' };
};

/** Type guard for a value that came out of a rejected create (e.g. caught from `.unwrap()`). */
export const isDBCreateError = (value: unknown): value is DBCreateError => {
  if (!isRecord(value)) return false;
  const kind = value['kind'];
  return (
    (kind === 'not_authenticated' || kind === 'server_rejected' || kind === 'network' || kind === 'unknown') &&
    typeof value['service'] === 'string' &&
    typeof value['message'] === 'string'
  );
};
